// DIRECT EXCEL NOTIFICATION - Fast direct data sending to users
import awsS3Service from './awsS3Service';
import userConsolidatedWorkbookService from './userConsolidatedWorkbookService';

export interface ExcelData {
  headers: string[];
  rows: string[][];
  sheetName: string;
  workbookId: string;
}

export interface DirectExcelNotification {
  id: string;
  userId: string;
  type: 'EXCEL_ASSIGNMENT';
  title: string;
  message: string;
  excelData: {
    headers: string[];
    rows: string[][];
    sheetName: string;
    workbookId: string;
  };
  assignedBy: string;
  createdAt: string;
  read: boolean;
}

class DirectExcelNotificationService {
  private static instance: DirectExcelNotificationService;
  private readonly S3_BUCKET = 'ettaskmanager';
  private readonly S3_PREFIX = 'direct-excel/';

  static getInstance(): DirectExcelNotificationService {
    if (!DirectExcelNotificationService.instance) {
      DirectExcelNotificationService.instance = new DirectExcelNotificationService();
    }
    return DirectExcelNotificationService.instance;
  }

  /**
   * Send Excel data directly to user - FAST METHOD
   */
  async sendDirectExcelNotification(
    userId: string,
    excelData: {
      headers: string[];
      rows: string[][];
      sheetName: string;
      workbookId: string;
    },
    assignedBy: string,
    rowIndex: number
  ): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('⚡ DirectExcelNotification: Sending direct Excel notification to user:', userId);

      // Find the specific row data for this user
      const userRowData = excelData.rows[rowIndex] || excelData.rows[0] || [];
      
      console.log('⚡ DirectExcelNotification: Row data being sent:', userRowData);
      console.log('⚡ DirectExcelNotification: Row index:', rowIndex);
      console.log('⚡ DirectExcelNotification: Total rows:', excelData.rows.length);
      
      // Create unique ID based on workbook + sheet + row data to prevent duplicates
      const rowDataHash = JSON.stringify(userRowData);
      const uniqueId = `direct_excel_${excelData.workbookId}_${excelData.sheetName}_${rowDataHash.substring(0, 20)}_${Date.now()}`;
      
      // Create direct notification
      const notification: DirectExcelNotification = {
        id: uniqueId,
        userId,
        type: 'EXCEL_ASSIGNMENT',
        title: `📋 Excel Task Assignment`,
        message: `You have been assigned a new task in ${excelData.sheetName}. Please check the details.`,
        excelData: {
          headers: excelData.headers,
          rows: [userRowData], // Only send the specific row for this user
          sheetName: excelData.sheetName,
          workbookId: excelData.workbookId
        },
        assignedBy,
        createdAt: new Date().toISOString(),
        read: false
      };

      console.log('⚡ DirectExcelNotification: Notification data:', notification);
      console.log('⚡ DirectExcelNotification: Unique ID:', uniqueId);

      // Check if AWS S3 is configured
      if (!awsS3Service.isConfigured()) {
        console.log('⚠️ DirectExcelNotification: AWS S3 not configured, using fallback to old notification system');
        return this.sendFallbackNotification(userId, excelData, assignedBy, rowIndex);
      }

      // Save directly to AWS S3
      const s3Key = `${this.S3_PREFIX}${userId}/${notification.id}.json`;
      await awsS3Service.uploadStringContent(
        this.S3_BUCKET,
        s3Key,
        JSON.stringify(notification, null, 2),
        'application/json'
      );

      console.log('✅ DirectExcelNotification: Notification saved to AWS S3 successfully');

      // Also save to localStorage for immediate access
      this.saveToLocalStorage(notification);

      // **NEW FEATURE**: Add task to user's consolidated workbook
      try {
        // Get user name from notification context (you might need to pass this)
        const userName = userId; // TODO: Get actual user name from user service
        
        await userConsolidatedWorkbookService.addOrUpdateSheet(
          userId,
          userName,
          excelData.sheetName,
          {
            headers: excelData.headers,
            row: userRowData,
            workbookId: excelData.workbookId,
            assignedBy: assignedBy
          }
        );
        
        console.log(`📚 Task added to user's consolidated workbook in sheet: ${excelData.sheetName}`);
      } catch (workbookError) {
        console.warn('⚠️ Failed to add to consolidated workbook, but notification was sent:', workbookError);
      }

      return { success: true };

    } catch (error) {
      console.error('❌ DirectExcelNotification: Error sending direct notification:', error);
      
      // Fallback to old notification system
      return this.sendFallbackNotification(userId, excelData, assignedBy, rowIndex);
    }
  }

  /**
   * Fallback to old notification system when AWS S3 is not available
   */
  private async sendFallbackNotification(
    userId: string, 
    excelData: ExcelData, 
    assignedBy: string,
    rowIndex: number
  ): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('🔄 DirectExcelNotification: Using fallback to old notification system for sending');
      
      // Import the old notification service dynamically to avoid circular dependencies
      const { excelAwsNotificationService } = await import('./excelAwsNotificationService');
      
      // Convert new format to old format
      const rowData = excelData.rows[rowIndex] || [];
      const oldData = {
        fieldName: 'Task Assignment',
        fieldValue: rowData[0] || 'New Assignment',
        sheetName: excelData.sheetName,
        itemId: excelData.workbookId,
        assignedBy: assignedBy,
        cellAddress: `A${rowIndex + 1}`
      };

      await excelAwsNotificationService.sendExcelAssignmentNotification(
        userId,
        oldData.fieldName,
        oldData.fieldValue,
        oldData.sheetName,
        oldData.itemId,
        oldData.assignedBy,
        oldData.cellAddress
      );

      console.log('✅ DirectExcelNotification: Fallback notification sent successfully');
      return { success: true };

    } catch (error) {
      console.error('❌ DirectExcelNotification: Fallback sending also failed:', error);
      return { success: false, error: error instanceof Error ? error.message : String(error) };
    }
  }

  /**
   * Get direct Excel notifications for user - FAST METHOD
   */
  async getUserExcelNotifications(userId: string): Promise<DirectExcelNotification[]> {
    try {
      console.log('⚡ DirectExcelNotification: Getting notifications for user:', userId);

      // First check localStorage for speed
      const localNotifications = this.getFromLocalStorage(userId);
      if (localNotifications.length > 0) {
        console.log('⚡ DirectExcelNotification: Found local notifications:', localNotifications.length);
        return localNotifications;
      }

      // Check if AWS S3 is configured
      if (!awsS3Service.isConfigured()) {
        console.log('⚠️ DirectExcelNotification: AWS S3 not configured, using fallback to old notification system');
        return this.getFallbackNotifications(userId);
      }

      // Then check AWS S3
      const s3Key = `${this.S3_PREFIX}${userId}/`;
      const files = await awsS3Service.listFiles(this.S3_BUCKET, s3Key);
      
      console.log(`⚡ DirectExcelNotification: Found ${files.length} notification files in AWS S3`);

      const notifications: DirectExcelNotification[] = [];

      // CRITICAL FIX: Load ALL files, not just 10
      // Users need to see ALL their assigned tasks, not just recent ones
      const recentFiles = files
        .filter(file => file.LastModified)
        .sort((a, b) => new Date(b.LastModified!).getTime() - new Date(a.LastModified!).getTime());

      for (const file of recentFiles) {
        try {
          const fileContent = await awsS3Service.downloadFile(this.S3_BUCKET, file.Key);
          const notification: DirectExcelNotification = JSON.parse(fileContent);
          notifications.push(notification);
        } catch (error) {
          console.error('❌ DirectExcelNotification: Error loading file:', file.Key, error);
        }
      }

      console.log(`⚡ DirectExcelNotification: Loaded ${notifications.length} notifications for user`);
      return notifications;

    } catch (error) {
      console.error('❌ DirectExcelNotification: Error getting user notifications:', error);
      
      // Fallback to old notification system
      return this.getFallbackNotifications(userId);
    }
  }

  /**
   * Fallback to old notification system when AWS S3 is not available
   */
  private async getFallbackNotifications(userId: string): Promise<DirectExcelNotification[]> {
    try {
      console.log('🔄 DirectExcelNotification: Using fallback to old notification system');
      
      // Import the old notification service dynamically to avoid circular dependencies
      const { excelAwsNotificationService } = await import('./excelAwsNotificationService');
      const oldNotifications = await excelAwsNotificationService.getNotifications(userId);
      
      // Convert old notifications to new format
      const directNotifications: DirectExcelNotification[] = oldNotifications
        .filter(notification => notification.type === 'EXCEL_ASSIGNMENT')
        .map(notification => ({
          id: notification.id,
          userId: notification.userId,
          type: 'EXCEL_ASSIGNMENT' as const,
          title: notification.title,
          message: notification.message,
          timestamp: notification.createdAt,
          read: notification.read || false,
          assignedBy: 'System',
          createdAt: notification.createdAt || new Date().toISOString(),
          excelData: {
            headers: ['Particulars', 'Concerned Person', 'Status', 'Priority', 'Due Date'],
            rows: [notification.data ? [
              notification.data.fieldValue || notification.data.fieldName || 'Task Assignment', // Particulars
              notification.data.assignedBy || notification.data.userId || 'Unknown', // Concerned Person  
              notification.data.Status || 'TO-DO', // Status
              notification.data.Priority || 'Medium', // Priority
              notification.data['Due Date'] || notification.data.Date || new Date().toISOString().split('T')[0] // Due Date
            ] : ['Task Assignment', 'Unknown', 'TO-DO', 'Medium', new Date().toISOString().split('T')[0]]],
            sheetName: notification.data?.sheetName || 'Task Assignment',
            workbookId: notification.data?.itemId || 'unknown'
          }
        }));

      console.log(`🔄 DirectExcelNotification: Converted ${directNotifications.length} old notifications`);
      
      // Debug: Show what data was converted
      directNotifications.forEach((notification, index) => {
        console.log(`📊 Fallback Notification ${index + 1}:`, {
          id: notification.id,
          particulars: notification.excelData.rows[0]?.[0] || 'Empty',
          concernedPerson: notification.excelData.rows[0]?.[1] || 'Empty',
          status: notification.excelData.rows[0]?.[2] || 'Empty',
          priority: notification.excelData.rows[0]?.[3] || 'Empty',
          dueDate: notification.excelData.rows[0]?.[4] || 'Empty'
        });
      });
      
      return directNotifications;

    } catch (error) {
      console.error('❌ DirectExcelNotification: Fallback also failed:', error);
      return [];
    }
  }

  /**
   * Save notification to localStorage for fast access
   */
  private saveToLocalStorage(notification: DirectExcelNotification): void {
    try {
      const key = `direct_excel_${notification.userId}`;
      const existing = JSON.parse(localStorage.getItem(key) || '[]');
      existing.push(notification);
      
      // CRITICAL FIX: Keep ALL notifications, not just 20
      // Users need to see all their assigned tasks from all time
      localStorage.setItem(key, JSON.stringify(existing));
      
      console.log('✅ DirectExcelNotification: Saved to localStorage');
    } catch (error) {
      console.error('❌ DirectExcelNotification: Error saving to localStorage:', error);
    }
  }

  /**
   * Get notifications from localStorage
   */
  private getFromLocalStorage(userId: string): DirectExcelNotification[] {
    try {
      const key = `direct_excel_${userId}`;
      const notifications = JSON.parse(localStorage.getItem(key) || '[]');
      // CRITICAL FIX: Return ALL notifications, not just unread ones
      // Users need to see all their tasks, including previously viewed ones
      return notifications;
    } catch (error) {
      console.error('❌ DirectExcelNotification: Error reading from localStorage:', error);
      return [];
    }
  }

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId: string, userId: string): Promise<void> {
    try {
      // Update in localStorage
      const key = `direct_excel_${userId}`;
      const notifications = JSON.parse(localStorage.getItem(key) || '[]');
      const updated = notifications.map((n: DirectExcelNotification) => 
        n.id === notificationId ? { ...n, read: true } : n
      );
      localStorage.setItem(key, JSON.stringify(updated));

      console.log('✅ DirectExcelNotification: Marked as read');
    } catch (error) {
      console.error('❌ DirectExcelNotification: Error marking as read:', error);
    }
  }
}

export const directExcelNotification = DirectExcelNotificationService.getInstance();
